/**
 * Shopify Variant Image Tracking Service
 * 
 * This service handles tracking and maintaining image-variant associations when creating new products,
 * especially when options and variants can be edited or translated.
 */

export class ShopifyVariantImageTrackingService {
  constructor() {
    this.variantImageMap = new Map();
    this.optionValueToImageMap = new Map();
    this.originalToNewVariantMap = new Map();
    this.originalToNewImageMap = new Map(); // New map to track original to new image mapping
    this.originalVariants = null;
    this.originalImages = null;
    this.variantMap = null;
  }

  /**
   * Initializes tracking for a product's variants and images
   * @param {Object} product - Product object with variants
   * @param {Array} images - Array of product images
   */
  initializeTracking(product, images) {
    console.log('Initializing variant-image tracking');
    console.log(`Product has ${product?.variants ? product.variants.length : 0} variants`);
    console.log(`Processing ${images ? images.length : 0} images`);
    
    this.reset(); // Clear any existing tracking data
    
    if (!product || !product.variants || !images) {
      console.log('Missing required data for tracking initialization');
      return;
    }
    
    const originalVariants = product.variants;
    this.originalVariants = originalVariants;
    this.originalImages = images;
    
    // Create a map of option combinations to variant IDs for quick lookup
    this.variantMap = new Map();
    
    // Track image associations directly from the original images
    let variantImageAssociationsCount = 0;
    
    // Log variant and image data for debugging
    console.log('Original variants:', JSON.stringify(originalVariants.map(v => ({ id: v.id, options: v.selectedOptions || v.selected_options }))));
    console.log('Original images:', JSON.stringify(images.map(img => ({ id: img.id, src: img.src, variantIds: img.variantIds || img.variant_ids }))));
    
    // First, check if variants have direct image associations
    originalVariants.forEach((variant, variantIndex) => {
      // Check if variant has an image property
      if (variant.image && variant.image.id) {
        console.log(`Variant ${variant.id} has direct image association: ${variant.image.id}`);
        
        // Find the matching image in our images array
        const matchingImage = images.find(img => img.id === variant.image.id);
        if (matchingImage) {
          // Ensure the image has a variantIds array
          if (!matchingImage.variantIds) {
            matchingImage.variantIds = [];
          }
          
          // Add the variant ID to the image's variantIds if not already there
          if (!matchingImage.variantIds.includes(variant.id)) {
            matchingImage.variantIds.push(variant.id);
            console.log(`Added variant ${variant.id} to image ${matchingImage.id} variantIds`);
          }
          
          // Also track this association in our map
          this._addVariantImageAssociation(variant.id, matchingImage);
          variantImageAssociationsCount++;
        }
      }
    });
    
    // Then, check if images have direct variant associations
    images.forEach((image, imageIndex) => {
      // Check for variant IDs using different field naming conventions
      const variantIds = image.variantIds || image.variant_ids || [];
      
      console.log(`Image ${imageIndex} (${image.src || image.filename || 'unknown'}) has ${variantIds.length} direct variant associations`);
      
      if (variantIds.length > 0) {
        // Process direct variant associations
        console.log(`Image ${imageIndex} variant IDs:`, JSON.stringify(variantIds));
        variantIds.forEach(variantId => {
          this._addVariantImageAssociation(variantId, image);
          variantImageAssociationsCount++;
          
          // Find the variant and extract its option values for option-based tracking
          const variant = originalVariants.find(v => v.id === variantId);
          // Check for selected options using different field naming conventions
          const selectedOptions = variant?.selectedOptions || variant?.selected_options;
          
          if (variant && selectedOptions) {
            this._trackOptionValueImageAssociations(selectedOptions, image);
          }
        });
      }
    });
    
    // If we still don't have enough associations, fall back to position-based matching
    if (variantImageAssociationsCount < originalVariants.length) {
      console.log('Not enough direct associations found. Adding position-based associations...');
      
      // Calculate how many variants should be assigned to each image
      const variantsPerImage = Math.ceil(originalVariants.length / images.length);
      
      images.forEach((image, imageIndex) => {
        // Calculate which variants should be assigned to this image
        const startIdx = imageIndex * variantsPerImage;
        const endIdx = Math.min(startIdx + variantsPerImage, originalVariants.length);
        
        // Get the variants for this image
        for (let i = startIdx; i < endIdx; i++) {
          const variant = originalVariants[i];
          
          // Only add if we don't already have an association for this variant
          if (!this.variantImageMap.has(variant.id)) {
            console.log(`Adding position-based association: Image ${imageIndex} -> Variant ${variant.id} (index ${i})`);
            
            // Add to our tracking map
            this._addVariantImageAssociation(variant.id, image);
            variantImageAssociationsCount++;
            
            // Also add to the image's variantIds array for direct tracking
            if (!image.variantIds) {
              image.variantIds = [];
            }
            if (!image.variantIds.includes(variant.id)) {
              image.variantIds.push(variant.id);
            }
            
            // Track option value associations if available
            if (variant.selectedOptions || variant.selected_options) {
              this._trackOptionValueImageAssociations(variant.selectedOptions || variant.selected_options, image);
            }
          }
        }
      });
    }
    
    // Log the final variant-image map for debugging
    console.log('Final variant-image associations:');
    this.variantImageMap.forEach((images, variantId) => {
      console.log(`Variant ${variantId} is associated with ${images.length} images:`, 
                 JSON.stringify(images.map(img => ({ id: img.id, src: img.src || img.filename }))));
    });
    
    console.log(`Tracking initialized with ${variantImageAssociationsCount} variant-image associations`);
    console.log(`Option value tracking contains ${this.optionValueToImageMap.size} option-value associations`);
  }

  /**
   * Maps original variants to new variants based on their position/index
   * @param {Array} originalVariants - Original product variants
   * @param {Array} newVariants - Newly created variants
   * @param {Object} optionChanges - Changes made to options (names and values)
   * @returns {Map} - Mapping of original variant IDs to new variant IDs
   */
  mapOriginalToNewVariants(originalVariants, newVariants, optionChanges = {}) {
    console.log('Mapping original variants to new variants using position-based approach');
    console.log(`Original variants: ${originalVariants ? originalVariants.length : 0}`);
    console.log(`New variants: ${newVariants ? newVariants.length : 0}`);
    
    this.originalToNewVariantMap.clear();
    
    if (!originalVariants || !newVariants) {
      console.log('Missing variant data for mapping');
      return this.originalToNewVariantMap;
    }

    // If we're working with the same variants (e.g., in a product update scenario)
    // where the original variants are the same as the new variants, create a direct mapping
    if (originalVariants === newVariants || 
        (originalVariants.length === newVariants.length && 
         originalVariants[0].id === newVariants[0].id)) {
      console.log('Original variants are the same as new variants, creating direct mapping');
      originalVariants.forEach(variant => {
        this.originalToNewVariantMap.set(variant.id, variant.id);
      });
      return this.originalToNewVariantMap;
    }

    // Map variants based on their position/index
    // This assumes that new variants are created in the same order as original variants
    const minLength = Math.min(originalVariants.length, newVariants.length);
    
    for (let i = 0; i < minLength; i++) {
      const originalVariant = originalVariants[i];
      const newVariant = newVariants[i];
      
      if (originalVariant && newVariant) {
        this.originalToNewVariantMap.set(originalVariant.id, newVariant.id);
        console.log(`Mapped original variant ${originalVariant.id} (index ${i}) to new variant ${newVariant.id}`);
      }
    }
    
    console.log(`Created mapping for ${this.originalToNewVariantMap.size} variants`);
    return this.originalToNewVariantMap;
  }

  /**
   * Maps original images to newly uploaded media
   * @param {Array} originalImages - Original product images
   * @param {Array} newMedia - Newly uploaded media
   */
  mapOriginalToNewMedia(originalImages, newMedia) {
    console.log(`Mapping ${originalImages.length} original images to ${newMedia.length} new media items`);
    
    // Reset the mapping
    this.originalToNewImageMap = new Map();
    
    // First, try to map by position (most reliable method)
    if (originalImages.length === newMedia.length) {
      console.log('Image counts match, mapping by position');
      
      originalImages.forEach((originalImage, index) => {
        if (index < newMedia.length) {
          const newMediaItem = newMedia[index];
          
          // Map the original image ID to the new media ID
          this.originalToNewImageMap.set(originalImage.id, newMediaItem.id);
          console.log(`Position-based mapping: Original image ${originalImage.id} -> New media ${newMediaItem.id}`);
          
          // Copy variant associations from original image to new media
          this._copyVariantAssociations(originalImage, newMediaItem);
          
          // Update the new media item with the original source for reference
          newMediaItem.originalSource = originalImage.src || originalImage.url;
        }
      });
      
      console.log(`Mapped ${this.originalToNewImageMap.size} original images to new media by position`);
      return;
    }
    
    // If position mapping failed, try to map by filename/source
    console.log('Image counts don\'t match, attempting to map by filename/source');
    
    // Create a map of original image sources for quick lookup
    const originalImageSources = new Map();
    originalImages.forEach(img => {
      const source = img.src || img.url;
      if (source) {
        // Extract filename from source URL
        const filename = this._extractFilename(source);
        if (filename) {
          originalImageSources.set(filename, img);
          console.log(`Original image ${img.id}: filename=${filename}`);
        }
      }
    });
    
    // Try to match new media to original images by filename
    newMedia.forEach(newMediaItem => {
      const source = newMediaItem.originalSource || newMediaItem.src || newMediaItem.url;
      if (!source) {
        console.log(`New media ${newMediaItem.id} has no source URL to match`);
        return;
      }
      
      // Extract filename from source URL
      const filename = this._extractFilename(source);
      if (!filename) {
        console.log(`Could not extract filename from ${source}`);
        return;
      }
      
      console.log(`New media ${newMediaItem.id}: filename=${filename}`);
      
      // Try to find an exact match first
      if (originalImageSources.has(filename)) {
        const originalImage = originalImageSources.get(filename);
        this.originalToNewImageMap.set(originalImage.id, newMediaItem.id);
        console.log(`Exact filename match: Original image ${originalImage.id} -> New media ${newMediaItem.id}`);
        
        // Copy variant associations from original image to new media
        this._copyVariantAssociations(originalImage, newMediaItem);
        
        // Update the new media item with the original source for reference
        newMediaItem.originalSource = originalImage.src || originalImage.url;
        return;
      }
      
      // If no exact match, try fuzzy matching
      let bestMatch = null;
      let bestScore = 0;
      
      originalImageSources.forEach((originalImage, originalFilename) => {
        // Skip if this original image is already mapped
        if (Array.from(this.originalToNewImageMap.keys()).includes(originalImage.id)) {
          return;
        }
        
        // Calculate similarity score (simple implementation)
        const score = this._calculateSimilarity(filename, originalFilename);
        if (score > bestScore && score > 0.7) { // Threshold for similarity
          bestScore = score;
          bestMatch = originalImage;
        }
      });
      
      if (bestMatch) {
        this.originalToNewImageMap.set(bestMatch.id, newMediaItem.id);
        console.log(`Fuzzy filename match (score ${bestScore.toFixed(2)}): Original image ${bestMatch.id} -> New media ${newMediaItem.id}`);
        
        // Copy variant associations from original image to new media
        this._copyVariantAssociations(bestMatch, newMediaItem);
        
        // Update the new media item with the original source for reference
        newMediaItem.originalSource = bestMatch.src || bestMatch.url;
      }
    });
    
    // Check for unmapped original images and new media
    const unmappedOriginalImages = originalImages.filter(img => 
      !Array.from(this.originalToNewImageMap.keys()).includes(img.id)
    );
    
    const mappedNewMediaIds = Array.from(this.originalToNewImageMap.values());
    const unmappedNewMedia = newMedia.filter(media => 
      !mappedNewMediaIds.includes(media.id)
    );
    
    console.log(`${unmappedOriginalImages.length} unmapped original images, ${unmappedNewMedia.length} unmapped new media`);
    
    // If we have unmapped items on both sides, try to pair them up by position
    if (unmappedOriginalImages.length > 0 && unmappedNewMedia.length > 0) {
      console.log('Attempting to map remaining items by position');
      
      unmappedOriginalImages.forEach((unmappedOriginalImage, index) => {
        if (index < unmappedNewMedia.length) {
          const newMediaItem = unmappedNewMedia[index];
          
          this.originalToNewImageMap.set(unmappedOriginalImage.id, newMediaItem.id);
          console.log(`Fallback position mapping: Original image ${unmappedOriginalImage.id} -> New media ${newMediaItem.id}`);
          
          // Copy variant associations from original image to new media
          this._copyVariantAssociations(unmappedOriginalImage, newMediaItem);
          
          // Update the new media item with the original source for reference
          newMediaItem.originalSource = unmappedOriginalImage.src || unmappedOriginalImage.url;
        }
      });
    }
    
    console.log(`Mapped ${this.originalToNewImageMap.size} original images to new media`);
    
    // Log the final mappings
    this.originalToNewImageMap.forEach((newMediaId, originalImageId) => {
      console.log(`Original image ${originalImageId} -> New media ${newMediaId}`);
    });
  }
  
  /**
   * Generates image-variant associations based on tracking data
   * @param {Array} uploadedMedia - The uploaded media items
   * @param {Array} createdVariants - The created variants
   * @returns {Array} - Array of {variantId, mediaId} associations
   */
  getImageVariantAssociations(images, variants) {
    console.log(`Generating image-variant associations for ${images.length} images and ${variants.length} variants`);
    
    // Create a map to store associations between images and variants
    const associations = [];
    
    // Create a map of original variant IDs to new variant IDs
    // This is crucial for mapping the original associations to the new variants
    const variantIdMap = new Map();
    
    // Map original variants to new variants by position/index
    // Since we don't have a reliable way to match by ID, we'll use position
    if (this.originalVariants && this.originalVariants.length > 0 && variants && variants.length > 0) {
      console.log('Creating mapping from original variant IDs to new variant IDs');
      
      // If counts match, we can do a direct position-based mapping
      if (this.originalVariants.length === variants.length) {
        this.originalVariants.forEach((originalVariant, index) => {
          if (index < variants.length) {
            variantIdMap.set(originalVariant.id, variants[index].id);
            console.log(`Mapped original variant ${originalVariant.id} -> new variant ${variants[index].id}`);
          }
        });
      } else {
        // If counts don't match, try to match by options/attributes
        console.log('Variant counts don\'t match, attempting to match by options');
        
        this.originalVariants.forEach(originalVariant => {
          // Get option values from the original variant
          const originalOptions = originalVariant.selectedOptions || originalVariant.selected_options || [];
          if (originalOptions.length === 0) {
            console.log(`Original variant ${originalVariant.id} has no options to match`);
            return;
          }
          
          // Find a matching variant in the new variants
          const matchingVariant = variants.find(newVariant => {
            const newOptions = newVariant.selectedOptions || newVariant.selected_options || [];
            if (newOptions.length !== originalOptions.length) return false;
            
            // Check if all options match
            return originalOptions.every(originalOpt => {
              return newOptions.some(newOpt => 
                newOpt.name === originalOpt.name && 
                newOpt.value === originalOpt.value
              );
            });
          });
          
          if (matchingVariant) {
            variantIdMap.set(originalVariant.id, matchingVariant.id);
            console.log(`Matched by options: original variant ${originalVariant.id} -> new variant ${matchingVariant.id}`);
          } else {
            console.log(`No matching variant found for original variant ${originalVariant.id}`);
          }
        });
      }
      
      console.log(`Created ${variantIdMap.size} variant ID mappings`);
    }
    
    // First, check if images have direct variant associations from the original product
    let hasDirectAssociations = false;
    images.forEach((image, imageIndex) => {
      const variantIds = image.variantIds || image.variant_ids || [];
      if (variantIds.length > 0) {
        hasDirectAssociations = true;
        console.log(`Image ${imageIndex} (${image.src || image.originalSource}) has ${variantIds.length} direct variant associations`);
        
        // Map the original variant IDs to new variant IDs
        const mappedVariantIds = [];
        variantIds.forEach(originalVariantId => {
          // If we have a mapping for this variant ID, use it
          if (variantIdMap.has(originalVariantId)) {
            const newVariantId = variantIdMap.get(originalVariantId);
            mappedVariantIds.push(newVariantId);
            console.log(`Mapped original variant ID ${originalVariantId} -> new variant ID ${newVariantId}`);
          } else {
            console.log(`No mapping found for original variant ID ${originalVariantId}`);
          }
        });
        
        // Only add the association if we have mapped variant IDs
        if (mappedVariantIds.length > 0) {
          associations.push({
            imageId: image.id,
            variantIds: mappedVariantIds,
            imageIndex
          });
        }
      }
    });
    
    // If we don't have direct associations, use position-based matching
    if (!hasDirectAssociations || associations.length === 0) {
      console.log('No direct associations found or all mappings failed. Using position-based matching...');
      
      // If we have more variants than images, we need to assign multiple variants to each image
      const variantsPerImage = Math.ceil(variants.length / images.length);
      console.log(`Assigning approximately ${variantsPerImage} variants per image`);
      
      images.forEach((image, imageIndex) => {
        // Calculate which variants should be assigned to this image
        const startIdx = imageIndex * variantsPerImage;
        const endIdx = Math.min(startIdx + variantsPerImage, variants.length);
        
        // Get the variants for this image
        const imageVariants = variants.slice(startIdx, endIdx);
        
        if (imageVariants.length > 0) {
          const variantIds = imageVariants.map(v => v.id);
          console.log(`Assigning image ${imageIndex} (${image.id}) to variants ${startIdx}-${endIdx-1} (${variantIds.length} variants)`);
          
          associations.push({
            imageId: image.id,
            variantIds: variantIds,
            imageIndex
          });
        }
      });
    }
    
    // Log the final associations
    console.log(`Created ${associations.length} image-variant associations`);
    associations.forEach((assoc, index) => {
      console.log(`Image ${index} (${assoc.imageId}) is associated with ${assoc.variantIds.length} variants`);
    });
    
    return associations;
  }

  /**
   * Resets all tracking data
   */
  reset() {
    this.variantImageMap.clear();
    this.optionValueToImageMap.clear();
    this.originalToNewVariantMap.clear();
    this.originalToNewImageMap.clear(); // Clear the new map as well
    this.originalVariants = null;
    this.originalImages = null;
    this.variantMap = null;
  }

  /**
   * Creates a standardized key from selected options
   * @param {Array} selectedOptions - Array of {name, value} pairs
   * @returns {String} - Standardized option combination key
   * @private
   */
  _createOptionComboKey(selectedOptions) {
    // Sort by option name to ensure consistent ordering
    const sortedOptions = [...selectedOptions].sort((a, b) => a.name.localeCompare(b.name));
    return sortedOptions.map(opt => `${opt.name}:${opt.value}`).join('|');
  }

  /**
   * Adds a variant-image association to the tracking map
   * @param {String} variantId - Variant ID
   * @param {Object} image - Image object
   * @private
   */
  _addVariantImageAssociation(variantId, image) {
    if (!this.variantImageMap.has(variantId)) {
      this.variantImageMap.set(variantId, []);
    }
    this.variantImageMap.get(variantId).push(image);
  }

  /**
   * Tracks associations between option values and images
   * @param {Array} selectedOptions - Array of {name, value} pairs
   * @param {Object} image - Image object
   * @private
   */
  _trackOptionValueImageAssociations(selectedOptions, image) {
    selectedOptions.forEach(option => {
      this._addOptionValueImageAssociation(option.name, option.value, image);
    });
  }

  /**
   * Adds an option value-image association to the tracking map
   * @param {String} optionName - Option name
   * @param {String} optionValue - Option value
   * @param {Object} image - Image object
   * @private
   */
  _addOptionValueImageAssociation(optionName, optionValue, image) {
    const key = `${optionName}:${optionValue}`;
    if (!this.optionValueToImageMap.has(key)) {
      this.optionValueToImageMap.set(key, []);
    }
    this.optionValueToImageMap.get(key).push(image);
  }

  /**
   * Checks if a filename matches an option value
   * @param {String} filename - Image filename or source
   * @param {String} optionValue - Option value to check
   * @returns {Boolean} - True if the filename matches the option value
   * @private
   */
  _filenameMatchesOptionValue(filename, optionValue) {
    // Normalize inputs
    const normalizedFilename = filename.toLowerCase();
    const normalizedOptionValue = optionValue.toLowerCase();
    
    // Skip very short option values to avoid false positives
    if (normalizedOptionValue.length < 2) {
      return false;
    }
    
    // Log for debugging
    console.log(`Checking if filename '${normalizedFilename}' matches option value '${normalizedOptionValue}'`);
    
    // Check for exact word match with word boundaries
    try {
      const exactRegex = new RegExp(`\b${normalizedOptionValue}\b`, 'i');
      if (exactRegex.test(normalizedFilename)) {
        console.log(`Found exact match for '${normalizedOptionValue}' in filename`);
        return true;
      }
    } catch (error) {
      // If regex fails (e.g., due to special characters), fall back to includes
      console.log(`Regex error: ${error.message}, falling back to simple matching`);
    }
    
    // Check for option value with different separators
    const valueWithDash = normalizedOptionValue.replace(/ /g, '-');
    const valueWithUnderscore = normalizedOptionValue.replace(/ /g, '_');
    
    if (normalizedFilename.includes(valueWithDash)) {
      console.log(`Found match with dashes: '${valueWithDash}' in filename`);
      return true;
    }
    
    if (normalizedFilename.includes(valueWithUnderscore)) {
      console.log(`Found match with underscores: '${valueWithUnderscore}' in filename`);
      return true;
    }
    
    // Check for partial match anywhere in the filename
    // Only consider this a match if the option value is at least 4 characters
    // to avoid false positives with short strings
    if (normalizedOptionValue.length >= 4 && normalizedFilename.includes(normalizedOptionValue)) {
      console.log(`Found partial match for '${normalizedOptionValue}' in filename`);
      return true;
    }
    
    // No match found
    return false;
  }

  /**
   * Extracts option value changes from option changes object
   * @param {Object} optionChanges - Option changes object
   * @returns {Object} - Mapping of original option values to new option values
   * @private
   */
  _extractOptionValueChanges(optionChanges) {
    const valueChanges = {};
    
    // Process option_value_updates if present
    if (optionChanges.option_value_updates) {
      Object.entries(optionChanges.option_value_updates).forEach(([optionId, updates]) => {
        Object.entries(updates).forEach(([oldValue, newValue]) => {
          valueChanges[oldValue] = newValue;
        });
      });
    }
    
    // Process option1_values, option2_values, option3_values if present
    ['option1_values', 'option2_values', 'option3_values'].forEach((field, index) => {
      if (optionChanges[field]) {
        Object.entries(optionChanges[field]).forEach(([valueIndex, newValue]) => {
          // We need the original option values to map properly
          // This is a limitation - we can't know the original value without additional context
          // The caller should provide option_value_updates for proper mapping
        });
      }
    });
    
    return valueChanges;
  }

  /**
   * Applies option value changes to selected options
   * @param {Array} selectedOptions - Array of {name, value} pairs
   * @param {Object} valueChanges - Mapping of original option values to new option values
   * @returns {String} - New option combination key after applying changes
   * @private
   */
  _applyOptionValueChanges(selectedOptions, valueChanges) {
    const updatedOptions = selectedOptions.map(option => ({
      name: option.name,
      value: valueChanges[option.value] || option.value
    }));
    
    return this._createOptionComboKey(updatedOptions);
  }

  /**
   * Gets variants associated with an image
   * @param {Object} image - Image object
   * @returns {Array} - Array of variant IDs
   * @private
   */
  _getVariantsForImage(image) {
    const variantIds = [];
    
    this.variantImageMap.forEach((images, variantId) => {
      if (images.includes(image)) {
        variantIds.push(variantId);
      }
    });
    
    return variantIds;
  }

  /**
   * Gets option values associated with an image
   * @param {Object} image - Image object
   * @returns {Array} - Array of {optionName, optionValue} pairs
   * @private
   */
  _getOptionValuesForImage(image) {
    const optionValuePairs = [];
    
    this.optionValueToImageMap.forEach((images, key) => {
      if (images.includes(image)) {
        const [optionName, optionValue] = key.split(':');
        optionValuePairs.push({ optionName, optionValue });
      }
    });
    
    return optionValuePairs;
  }

  /**
   * Removes duplicate associations
   * @param {Array} associations - Array of associations
   * @returns {Array} - Array of unique associations
   * @private
   */
  _deduplicateAssociations(associations) {
    const uniqueAssociations = [];
    const seen = new Set();
    
    associations.forEach(assoc => {
      const key = `${assoc.variantId}:${assoc.mediaId}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueAssociations.push(assoc);
      }
    });
    
    return uniqueAssociations;
  }

  /**
   * Calculate string similarity between two strings (0-1)
   * @private
   */
  _calculateStringSimilarity(str1, str2) {
    if (!str1 || !str2) return 0;
    
    // Convert to lowercase for case-insensitive comparison
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();
    
    // If either string contains the other, consider it a strong match
    if (s1.includes(s2) || s2.includes(s1)) {
      return 0.9;
    }
    
    // Simple Levenshtein distance implementation
    const track = Array(s2.length + 1).fill(null).map(() => 
      Array(s1.length + 1).fill(null));
    
    for (let i = 0; i <= s1.length; i += 1) {
      track[0][i] = i;
    }
    
    for (let j = 0; j <= s2.length; j += 1) {
      track[j][0] = j;
    }
    
    for (let j = 1; j <= s2.length; j += 1) {
      for (let i = 1; i <= s1.length; i += 1) {
        const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
        track[j][i] = Math.min(
          track[j][i - 1] + 1, // deletion
          track[j - 1][i] + 1, // insertion
          track[j - 1][i - 1] + indicator, // substitution
        );
      }
    }
    
    const distance = track[s2.length][s1.length];
    const maxLength = Math.max(s1.length, s2.length);
    
    // Return similarity as 1 - normalized distance
    return maxLength === 0 ? 1 : 1 - distance / maxLength;
  }

  /**
   * Helper method to copy variant associations from original image to new media
   * @param {Object} originalImage - Original image
   * @param {Object} newMediaItem - New media item
   * @private
   */
  _copyVariantAssociations(originalImage, newMediaItem) {
    // Copy variant associations
    const variantIds = originalImage.variantIds || originalImage.variant_ids || [];
    if (variantIds.length > 0) {
      newMediaItem.variantIds = [...variantIds];
      console.log(`Copied ${variantIds.length} variant associations from original image ${originalImage.id} to new media ${newMediaItem.id}`);
    }
  }

  /**
   * Extracts filename from a URL
   * @param {string} url - URL to extract filename from
   * @returns {string} - Extracted filename
   * @private
   */
  _extractFilename(url) {
    if (!url) return null;
    
    try {
      // Remove query parameters
      const urlWithoutParams = url.split('?')[0];
      
      // Extract the filename from the path
      const parts = urlWithoutParams.split('/');
      const filename = parts[parts.length - 1];
      
      return filename;
    } catch (error) {
      console.warn(`Error extracting filename from ${url}:`, error);
      return null;
    }
  }

  /**
   * Calculates similarity between two strings (simple implementation)
   * @param {string} str1 - First string
   * @param {string} str2 - Second string
   * @returns {number} - Similarity score (0-1)
   * @private
   */
  _calculateSimilarity(str1, str2) {
    if (!str1 || !str2) return 0;
    
    // Simple implementation: count matching characters
    const len1 = str1.length;
    const len2 = str2.length;
    
    // If lengths are very different, similarity is low
    if (Math.abs(len1 - len2) / Math.max(len1, len2) > 0.5) {
      return 0;
    }
    
    // Count matching characters
    let matches = 0;
    const minLen = Math.min(len1, len2);
    
    for (let i = 0; i < minLen; i++) {
      if (str1[i] === str2[i]) {
        matches++;
      }
    }
    
    return matches / Math.max(len1, len2);
  }
}
